import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import applyChanges from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ApplyChanges";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useDebounce from "@WhiteCap/CustomHooks/useDebounce";
import loadCustomBillTos from "@WhiteCap/Handlers/BillTos/LoadCustomBillTos";
import loadCustomApplyChanges from "@WhiteCap/Handlers/LoadCustomApplyChanges";
import loadWarehouse from "@WhiteCap/Handlers/WarehouseHandler/LoadWarehouse";
import CustomCross from "@WhiteCap/Icons/CustomCross";
import ShipToModal from "@WhiteCap/MobiusOverrides/Modal/ShipToModal";
import { GetCustomBillTosApiParameter } from "@WhiteCap/Services/CustomCustomerService";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import omit from "lodash/omit";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    modalIsOpen: boolean;
    extendedStyles?: SelectShipToModalStyles;
    billToParameter: GetCustomBillTosApiParameter;
    onModalClose: () => void;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    showBackButton?: boolean;
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState, props: OwnProps) => {
    const customBillToDataview = state.WhiteCap.customBillToData;
    const pagination = state.WhiteCap.customBillToData.pagination;

    return {
        customBillToDataview,
        pagination,
        session: state.context.session,
        billTosById: state.data.billTos.byId,
    };
};

const mapDispatchToProps = {
    loadWarehouse,
    loadCustomBillTos,
    loadCustomApplyChanges,
    setDrawerIsOpen,
};

type Props = OwnProps &
    HasToasterContext &
    PaginationPresentationProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface SelectShipToModalStyles {
    searchCustomerText?: TextFieldPresentationProps;
    searchButton?: ButtonPresentationProps;
    container?: GridContainerProps;
    accountGridItem?: GridItemProps;
    searchGridItem?: GridItemProps;
    selectButton?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    customerNameText?: TypographyPresentationProps;
    selectButtonGridItem?: GridItemProps;
    customerNumberGridItem?: GridItemProps;
    customerNumberText?: TypographyPresentationProps;
    selectedCustomerNameGridItem?: GridItemProps;
    selectedCustomerNumberGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const selectShipToModalStyles: SelectShipToModalStyles = {
    container: {
        css: css`
            margin-bottom: 8px;
            padding: 8px;
            background-color: #f7f8f9;
            > div[class^="GridOffset"] {
                margin: 0px;
                max-width: 100%;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 200px;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    wrapper: {
        css: css`
            font-style: normal;
            p {
                font-size: 12px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        p {
                            font-size: 12px;
                        }
                    `,
                    css`
                        p {
                            font-size: 12px;
                        }
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    accountGridItem: {
        width: 9,
    },
    customerNumberGridItem: {
        width: 12,
        css: css`
            padding: 0px;
        `,
    },
    searchGridItem: {
        width: 12,
        css: css`
            height: 40px;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px !important;
        `,
    },
    selectButton: {
        variant: "tertiary",
        typographyProps: {
            css: css`
                font-size: 14px;
                text-align: center;
                font-weight: 450;
                line-height: 24px;
                text-transform: uppercase;
            `,
        },
        css: css`
            padding: 4px 8px;
            justify-content: center;
            align-items: center;
            min-width: max-content;
        `,
    },
    modal: {
        cssOverrides: {
            modalContent: css`
                padding: 16px 24px !important;
                > div[class^="GridOffset"] {
                    margin: 0px;
                }
            `,
            // modalTitle: css`
            //     display:flex;
            //     flex-direction: row-reverse;

            // `,
            // headlineTypography: css`
            //         margin-left: 10px;
            //         margin-bottom: 0px !important;
            //     `
        },
    },
    selectButtonGridItem: {
        width: 3,
        css: css`
            padding: 0px;
            align-items: center;
            justify-content: end;
        `,
    },
    customerNumberText: {
        css: css`
            font-size: 12px;
            font-weight: 450;
        `,
    },
    selectedCustomerNumberGridItem: {
        css: css`
            padding: 0px 15px;
        `,
    },
    customerNameText: {
        css: css`
            font-size: 12px;
            text-transform: uppercase;
        `,
    },
    selectedCustomerNameGridItem: {
        css: css`
            padding-top: 0px;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding-bottom: 8px !important;
        `,
    },
    titleText: {
        css: css`
            font-size: 14px;
            font-weight: 450;
        `,
    },
};

const SelectShipToModal = ({
    extendedStyles,
    modalIsOpen,
    onModalClose,
    customBillToDataview,
    loadWarehouse,
    loadCustomBillTos,
    loadCustomApplyChanges,
    billTo,
    billToParameter,
    onSelect,
    setDrawerIsOpen,
    toaster,
    pagination,
    showBackButton,
    billTosById,
    session,
}: Props) => {
    const [styles] = useState(() => mergeToNew(selectShipToModalStyles, extendedStyles));
    const [searchValue, setSearchValue] = React.useState("");
    const debouncedSearchValue = useDebounce(searchValue, 500);
    const [customBillToParameter, setBillToParameter] = useState<GetCustomBillTosApiParameter>({
        page: 1,
        pageSize: 8,
        customerNumber: billToParameter?.customerNumber ?? "",
    });

    const getApiParameter = (newBillToParameter: GetCustomBillTosApiParameter) => {
        const apiParameter = {
            ...newBillToParameter,
            page: newBillToParameter?.page ?? 1,
            expand: ["shipTos"],
        };
        return omit(apiParameter, ["additionalExpands"]);
    };

    const lastSearchRef = useRef<string>("");
    const billToId = session.billToId ?? "";
    const selectedBillTo = billTosById[billToId];

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }
        const initialParameter = getApiParameter(customBillToParameter);
        loadCustomBillTos({
            ...initialParameter,
            customerNumber: billToParameter.customerNumber ?? "",
        });
    }, [customBillToParameter]);

    const handleSetSearchFilter = () => {
        setSearchValue("");
    };

    const modalCloseHandler = () => {
        setSearchValue("");
        lastSearchRef.current = "";
        const closingParameter = omit(customBillToParameter, ["customerNumber", "filter"]);
        setBillToParameter({
            ...closingParameter,
            page: 1,
            additionalExpands: ["accounts"],
        });
        onModalClose();
    };

    const selectShipToHandler = async (billToId?: string) => {
        if (customBillToDataview.billTos && billToId) {
            let selectedBillTo: BillToModel | undefined = customBillToDataview.billTos.find(
                billTo => billTo.id === billToId,
            );

            if (!selectedBillTo && billTo?.id === billToId) {
                selectedBillTo = billTo;
            }

            if (selectedBillTo?.properties?.defaultWarehouse) {
                loadWarehouse({
                    warehouseId: selectedBillTo?.properties?.defaultWarehouse,
                    changeShipWarehouse: true,
                });
            }

            const selectedShipToId = selectedBillTo?.shipTos?.filter(ship => ship.id !== "")[0].id;

            if (selectedBillTo) {
                onSelect(selectedBillTo as BillToModel);
                await loadCustomApplyChanges({
                    shipToId: selectedShipToId,
                    onError: (errorMessage: string) => {
                        toaster.addToast({
                            messageType: "warning",
                            body: siteMessage("ChangeCustomer_Update_Failed", errorMessage),
                        });
                    },
                });
                setDrawerIsOpen({ isOpen: false });
            }
        }
    };

    useEffect(() => {
        if (!modalIsOpen) {
            return;
        }
        const current = debouncedSearchValue.trim();
        const shouldSearch = (current.length >= 3 || current.length === 0) && current !== lastSearchRef.current;
        if (shouldSearch) {
            lastSearchRef.current = current;
            setBillToParameter({
                ...customBillToParameter,
                filter: current,
                page: 1,
            });
        }
    }, [debouncedSearchValue, modalIsOpen]);

    function handleSearchAddressesChanged(event: ChangeEvent<HTMLInputElement>): void {
        event.stopPropagation();
        const value = event.target.value ?? "";
        setSearchValue(value);
    }
    const onChangePage = (page: number) => {
        setBillToParameter({
            ...customBillToParameter,
            page,
        });
    };

    const onChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        let newPageSize = Number(event.target.value);
        setBillToParameter({
            ...customBillToParameter,
            page: 1,
            customerNumber: billTo?.customerNumber ?? "",
            pageSize: newPageSize,
        });
    };
    const StyledAddress = getStyledWrapper("address");

    const customerNumber = billTo ? billTo?.customerNumber : selectedBillTo ? selectedBillTo?.customerNumber : "";
    const customerName = billTo
        ? billTo?.properties?.customerName
        : selectedBillTo
          ? selectedBillTo?.properties?.customerName
          : "";

    return (
        <ShipToModal
            showBackButton={showBackButton}
            isOpen={modalIsOpen}
            headline={translate("Ship-To")}
            handleClose={modalCloseHandler}
            {...styles.modal}
        >
            <GridContainer>
                <GridItem width={12} {...styles.titleGridItem}>
                    <Typography {...styles.titleText}>{translate("Ship-To Address(es) for Account")}</Typography>
                </GridItem>
                <GridItem width={6}>
                    <GridContainer>
                        <GridItem width={12} {...styles.selectedCustomerNumberGridItem}>
                            <Typography {...styles.customerNumberText}>{`#${translate(customerNumber)}`}</Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.selectedCustomerNameGridItem}>
                            <Typography {...styles.customerNameText}>{customerName}</Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.searchGridItem}>
                    <TextField
                        {...styles.searchCustomerText}
                        value={searchValue}
                        onChange={handleSearchAddressesChanged}
                        onSelect={() => selectShipToHandler}
                        iconProps={searchValue ? { src: CustomCross } : { src: "Search" }}
                        iconClickableProps={{
                            onClick: () => {
                                handleSetSearchFilter();
                            },
                            type: "button",
                        }}
                        placeholder={translate("Search by Name, Address, City")}
                    />
                </GridItem>
            </GridContainer>
            {customBillToDataview.isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {!customBillToDataview.isLoading &&
                customBillToDataview?.billTos &&
                customBillToDataview?.billTos.length === 0 && (
                    <Typography>{translate("No matching accounts found.")}</Typography>
                )}

            {customBillToDataview?.billTos
                ?.slice()
                .sort(
                    (a, b) =>
                        (!(
                            selectedBillTo?.customerNumber === a?.customerNumber &&
                            selectedBillTo?.address1 === a?.address1
                        )
                            ? 1
                            : 0) -
                        (!(
                            selectedBillTo?.customerNumber === b?.customerNumber &&
                            selectedBillTo?.address1 === b?.address1
                        )
                            ? 1
                            : 0),
                )
                .map((billTo, index) => (
                    <>
                        <GridContainer {...styles.container}>
                            <GridItem {...styles.accountGridItem}>
                                <GridContainer>
                                    <GridItem {...styles.customerNumberGridItem}>
                                        <Typography {...styles.customerNumberText}>{billTo?.companyName}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.customerNumberGridItem}>
                                        <StyledAddress
                                            {...styles.wrapper}
                                            data-test-selector="selectedAddressInfoDisplay"
                                        >
                                            <Typography as="p">{billTo?.address1}</Typography>
                                            <Typography as="p">
                                                {`${billTo?.city}, ${billTo?.state?.name}, ${billTo?.postalCode}`}
                                            </Typography>
                                        </StyledAddress>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            {!(
                                selectedBillTo?.customerNumber === billTo?.customerNumber &&
                                selectedBillTo?.address1 === billTo?.address1
                            ) && (
                                <GridItem {...styles.selectButtonGridItem}>
                                    <Button
                                        {...styles.selectButton}
                                        type="button"
                                        aria-label="Select Account"
                                        onClick={() => selectShipToHandler(billTo?.id)}
                                        data-test-selector="selectShipToModal_accountSelect"
                                    >
                                        {translate("Select")}
                                    </Button>
                                </GridItem>
                            )}
                        </GridContainer>
                    </>
                ))}
            {pagination && pagination.totalItemCount > (pagination.defaultPageSize ?? 8) && (
                <Pagination
                    currentPage={pagination.currentPage}
                    resultsPerPage={pagination!.pageSize ?? 8}
                    resultsCount={pagination!.totalItemCount}
                    resultsPerPageOptions={pagination!.pageSizeOptions}
                    onChangePage={onChangePage}
                    onChangeResultsPerPage={onChangeResultsPerPage}
                    prevPage={pagination!.prevPageUri}
                    nextPage={pagination!.nextPageUri}
                />
            )}
        </ShipToModal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(SelectShipToModal));
